import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import type { PortalChatMessage, PortalChatSendPayload } from '@/api/portalMessaging'
import { createUuid } from '@/utils/uuid'
import { isPortalPreviewActive, PortalPreviewReadonlyError } from '@/portal/portalPreview'

interface Draft {
  body: string
  files: File[]
  replyTo: PortalChatMessage | null
  attempt?: { id: string; fingerprint: string }
  updatedAt?: number
  missingFiles?: string[]
  metadata?: Record<string, unknown>
}
const empty = (): Draft => ({ body: '', files: [], replyTo: null })
interface StoredFile { name: string; type: string; lastModified: number; bytes: ArrayBuffer }
const fileBytes = new WeakMap<File, Promise<StoredFile>>()
function storeFile(file: File): Promise<StoredFile> {
  let stored = fileBytes.get(file)
  if (!stored) {
    stored = file.arrayBuffer().then(bytes => ({ name: file.name, type: file.type, lastModified: file.lastModified, bytes }))
    fileBytes.set(file, stored)
  }
  return stored
}
const accountEpochs = new Map<string, number>()
let database: Promise<IDBDatabase> | undefined
function db(): Promise<IDBDatabase> {
  return database ??= new Promise((resolve, reject) => {
    const request = indexedDB.open('avoschool-chat-drafts', 1)
    request.onupgradeneeded = () => request.result.createObjectStore('accounts')
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => { database = undefined; reject(request.error) }
  })
}
async function read(account: string): Promise<Record<string, Draft>> {
  const database = await db()
  return new Promise((resolve, reject) => {
    const request = database.transaction('accounts').objectStore('accounts').get(account)
    request.onsuccess = () => {
      const saved = (request.result ?? {}) as Record<string, Omit<Draft, 'files'> & { files: (File | StoredFile)[] }>
      resolve(Object.fromEntries(Object.entries(saved).map(([id, draft]) => [id, {
        ...draft, files: draft.files.map(file => file instanceof File ? file : new File([file.bytes], file.name, { type: file.type, lastModified: file.lastModified })),
      }])))
    }
    request.onerror = () => reject(request.error)
  })
}
async function write(account: string, drafts: Record<string, Draft>, epoch: number): Promise<void> {
  if (epoch !== (accountEpochs.get(account) ?? 0)) return
  const stored = Object.fromEntries(await Promise.all(Object.entries(drafts).map(async ([id, draft]) => [id, {
    ...draft, files: await Promise.all(draft.files.map(storeFile)),
  }])))
  const database = await db()
  if (epoch !== (accountEpochs.get(account) ?? 0)) return
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction('accounts', 'readwrite')
    const request = transaction.objectStore('accounts').put(stored, account)
    request.onerror = () => reject(request.error)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error)
  })
}
function snapshot(values: Record<string, Draft>): Record<string, Draft> {
  return Object.fromEntries(Object.entries(values).map(([id, value]) => [id, {
    body: value.body, files: [...value.files],
    replyTo: value.replyTo ? JSON.parse(JSON.stringify(value.replyTo)) : null,
    attempt: value.attempt ? { ...value.attempt } : undefined,
    updatedAt: value.updatedAt, missingFiles: [...(value.missingFiles ?? [])],
    metadata: value.metadata ? JSON.parse(JSON.stringify(value.metadata)) : undefined,
  }]))
}
const fileDigests = new WeakMap<File, Promise<string>>()
function fileDigest(file: File): Promise<string> {
  let digest = fileDigests.get(file)
  if (!digest) {
    digest = storeFile(file).then(async stored => {
      const hash = await crypto.subtle.digest('SHA-256', stored.bytes)
      return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('')
    })
    fileDigests.set(file, digest)
  }
  return digest
}
async function fingerprint(payload: PortalChatSendPayload, includeHomework = true): Promise<string> {
  const parts: unknown[] = [payload.body.trim(), payload.replyToMessageId ?? null,
    await Promise.all(payload.files.map(async file => [file.name, file.type, file.size, file.lastModified, await fileDigest(file)]))]
  if (includeHomework && (payload.homeworkLessonId || payload.homeworkId)) parts.push([payload.homeworkLessonId ?? null, payload.homeworkId ?? null])
  return JSON.stringify(parts)
}

/** Text, replies, attachment blobs and retry IDs survive reload, isolated by account. */
export function usePortalChatDrafts(accountId: MaybeRefOrGetter<string>, conversationId: MaybeRefOrGetter<string>) {
  const drafts = ref<Record<string, Draft>>({})
  const ready = ref(false)
  const storageError = ref(false)
  const saving = ref(false)
  let generation = 0
  let writes = Promise.resolve()
  let flushing = false
  let queuedWrite: { account: string; drafts: Record<string, Draft>; epoch: number } | undefined
  function persist(account: string, saved: Record<string, Draft>): void {
    // Keep only the newest snapshot behind the active transaction. Rewriting every
    // keystroke (including all attachment blobs) delays the send's durable retry ID.
    queuedWrite = { account, drafts: saved, epoch: accountEpochs.get(account) ?? 0 }
    saving.value = true
    if (flushing) return
    flushing = true
    writes = Promise.resolve().then(async () => {
      try {
        while (queuedWrite) {
          const pending = queuedWrite
          queuedWrite = undefined
          try { await write(pending.account, pending.drafts, pending.epoch) }
          catch { if (pending.account === toValue(accountId)) storageError.value = true }
        }
      } finally {
        flushing = false
        saving.value = false
      }
    })
  }
  watch(() => toValue(accountId), async account => {
    const current = ++generation
    ready.value = false
    drafts.value = {}
    storageError.value = false
    if (account && !isPortalPreviewActive()) {
      try {
        await writes
        const saved = await read(account).catch(() => { storageError.value = true; return {} as Record<string, Draft> })
        if (current !== generation) return
        const fallback = JSON.parse(localStorage.getItem(`avoschool.chat-drafts.${account}`) ?? '{}') as Record<string, Draft>
        for (const [id, value] of Object.entries(fallback)) {
          if ((value.updatedAt ?? 0) > (saved[id]?.updatedAt ?? 0)) saved[id] = value
        }
        drafts.value = saved
      } catch { if (current === generation) storageError.value = true }
    }
    if (current === generation) ready.value = true
  }, { immediate: true, flush: 'sync' })
  watch(drafts, values => {
    const account = toValue(accountId)
    if (!account || !ready.value || isPortalPreviewActive()) return
    const saved = snapshot(values)
    try {
      localStorage.setItem(`avoschool.chat-drafts.${account}`, JSON.stringify(Object.fromEntries(Object.entries(saved).map(([id, value]) => [id, {
        ...value, files: [], missingFiles: [...(value.missingFiles ?? []), ...value.files.map(file => file.name)],
      }]))))
    } catch { storageError.value = true }
    persist(account, saved)
  }, { deep: true, flush: 'sync' })
  const current = computed(() => drafts.value[toValue(conversationId)] ?? empty())
  function patch(value: Partial<Draft>): void {
    if (isPortalPreviewActive()) return
    const id = toValue(conversationId)
    if (id && ready.value) drafts.value[id] = { ...current.value, ...value, updatedAt: Math.max(Date.now(), (current.value.updatedAt ?? 0) + 1) }
  }
  const body = computed({ get: () => current.value.body, set: body => patch({ body }) })
  const files = computed({ get: () => current.value.files, set: files => patch({ files, missingFiles: (current.value.missingFiles ?? []).filter(name => !files.some(file => file.name === name)) }) })
  const replyTo = computed({ get: () => current.value.replyTo, set: replyTo => patch({ replyTo }) })
  const metadata = computed({ get: () => current.value.metadata ?? {}, set: metadata => patch({ metadata }) })
  async function prepare(payload: PortalChatSendPayload): Promise<PortalChatSendPayload> {
    if (isPortalPreviewActive()) throw new PortalPreviewReadonlyError()
    const account = toValue(accountId)
    const sourceGeneration = generation
    const id = toValue(conversationId)
    const saved = drafts.value[id]
    const key = await fingerprint(payload)
    const legacyKey = JSON.stringify([payload.body.trim(), payload.replyToMessageId ?? null,
      payload.files.map(file => [file.name, file.type, file.size, file.lastModified])])
    const fingerprints = payload.homeworkLessonId || payload.homeworkId ? [key] : [key, legacyKey]
    const attempt = saved?.attempt && fingerprints.includes(saved.attempt.fingerprint)
      ? { id: saved.attempt.id, fingerprint: key } : { id: createUuid(), fingerprint: key }
    // Hashing file bytes is async: switching chats must not write the retry ID to another draft.
    if (account === toValue(accountId) && sourceGeneration === generation && drafts.value[id] === saved) {
      drafts.value[id] = { ...(saved ?? empty()), attempt, updatedAt: Math.max(Date.now(), (saved?.updatedAt ?? 0) + 1) }
      await writes
    }
    return { ...payload, clientMessageId: attempt.id }
  }
  async function clearSent(account: string, id: string, payload: PortalChatSendPayload, stillCurrent: () => boolean = () => true): Promise<void> {
    if (account !== toValue(accountId) || !stillCurrent()) return
    const saved = drafts.value[id]
    const sourceGeneration = generation
    if (!saved) return
    const [actual, sent] = await Promise.all([
      fingerprint({ body: saved.body, files: saved.files, replyToMessageId: saved.replyTo?.id }, false), fingerprint(payload, false),
    ])
    if (account === toValue(accountId) && sourceGeneration === generation && drafts.value[id] === saved && actual === sent && stillCurrent()) {
      drafts.value[id] = { ...empty(), updatedAt: Math.max(Date.now(), (saved.updatedAt ?? 0) + 1) }
    }
  }

  return { body, files, replyTo, metadata, ready, saving, storageError, missingFiles: computed(() => current.value.missingFiles ?? []), discardMissing: () => patch({ missingFiles: [] }), prepare, clearSent, textFor: (id: string) => drafts.value[id]?.body ?? '' }
}

export async function purgePortalChatDrafts(accountId: string): Promise<void> {
  if (isPortalPreviewActive()) return
  accountEpochs.set(accountId, (accountEpochs.get(accountId) ?? 0) + 1)
  try { localStorage.removeItem(`avoschool.chat-drafts.${accountId}`) } catch { /* Storage can be disabled. */ }
  try {
    const database = await db()
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction('accounts', 'readwrite')
      transaction.objectStore('accounts').delete(accountId)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
    })
  } catch { /* Logout must remain available when storage is unavailable. */ }
}
